import {
  createContext,
  type FC,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react'
import { useSessionStorage } from '@dhl/use-storage'

type TrackingKey = {
  barcode: string
  postalCode?: string
}

const emptyTrackingKey: TrackingKey = {
  barcode: '',
  postalCode: undefined,
}

type TrackingKeyContextValue = readonly [TrackingKey, (trackingKey: TrackingKey) => void]

const TrackingKeyContext = createContext<TrackingKeyContextValue | undefined>(undefined)

/**
 * Single source of truth for the tracking key. All consumers share this one
 * session-storage-backed state instead of each maintaining their own copy,
 * which previously caused the copies to drift out of sync.
 */
export const TrackingKeyProvider: FC<PropsWithChildren> = ({ children }) => {
  const [sessionTrackingKey, setSessionTrackingKey] = useSessionStorage<TrackingKey>('trackingKey', emptyTrackingKey)

  const trackingKey = sessionTrackingKey ?? emptyTrackingKey

  const setTrackingKey = useCallback(
    (newTrackingKey: TrackingKey) => {
      // Compare against the single authoritative value (no stale per-instance
      // copies), so a corrected postal code is never skipped.
      if (newTrackingKey.barcode !== trackingKey.barcode || newTrackingKey.postalCode !== trackingKey.postalCode) {
        setSessionTrackingKey(newTrackingKey)
      }
    },
    [trackingKey.barcode, trackingKey.postalCode, setSessionTrackingKey]
  )

  const value = useMemo<TrackingKeyContextValue>(() => [trackingKey, setTrackingKey], [trackingKey, setTrackingKey])

  return <TrackingKeyContext.Provider value={value}>{children}</TrackingKeyContext.Provider>
}

export const useTrackingKey = (initialTrackingKey?: TrackingKey): TrackingKeyContextValue => {
  const context = useContext(TrackingKeyContext)

  if (!context) {
    throw new Error('useTrackingKey must be used within a TrackingKeyProvider')
  }

  const [trackingKey, setTrackingKey] = context
  const hasSeeded = useRef(false)

  // Preserve the previous behaviour where passing an initial tracking key seeds
  // it (e.g. when deep-linking straight to a track & trace page) as long as
  // nothing has been stored yet.
  useEffect(() => {
    if (!hasSeeded.current && initialTrackingKey && !trackingKey.barcode) {
      hasSeeded.current = true
      setTrackingKey(initialTrackingKey)
    }
  }, [initialTrackingKey, trackingKey.barcode, setTrackingKey])

  return context
}

/**
 * A record of known-valid barcode → postal code combinations, kept separate
 * from the single {@link TrackingKey} so multiple parcels can be tracked in the
 * same session. Since the postal code was removed from the T&T URLs, the
 * onboarding app (anonymous, no profile fallback) relies on this store to
 * resolve the postal code needed to fetch the full parcel view.
 */
type TrackingPostalCodes = Record<string, string>

type TrackingPostalCodeContextValue = {
  getPostalCode: (barcode: string) => string | undefined
  setPostalCode: (barcode: string, postalCode: string) => void
}

const TrackingPostalCodeContext = createContext<TrackingPostalCodeContextValue | undefined>(undefined)

// A dedicated storage key (not the legacy `trackingKey`) so we don't break the
// existing single-tracking-key behaviour.
const trackingPostalCodesKey = 'trackingPostalCodes'
const emptyTrackingPostalCodes: TrackingPostalCodes = {}

export const TrackingPostalCodeProvider: FC<PropsWithChildren> = ({ children }) => {
  const [postalCodes, setPostalCodes] = useSessionStorage<TrackingPostalCodes>(
    trackingPostalCodesKey,
    emptyTrackingPostalCodes
  )

  const store = postalCodes ?? emptyTrackingPostalCodes

  const getPostalCode = useCallback((barcode: string): string | undefined => store[barcode], [store])

  const setPostalCode = useCallback(
    (barcode: string, postalCode: string) => {
      if (!barcode || store[barcode] === postalCode) {
        return
      }
      setPostalCodes({ ...store, [barcode]: postalCode })
    },
    [store, setPostalCodes]
  )

  const value = useMemo<TrackingPostalCodeContextValue>(
    () => ({ getPostalCode, setPostalCode }),
    [getPostalCode, setPostalCode]
  )

  return <TrackingPostalCodeContext.Provider value={value}>{children}</TrackingPostalCodeContext.Provider>
}

export const useTrackingPostalCode = (): TrackingPostalCodeContextValue => {
  const context = useContext(TrackingPostalCodeContext)

  if (!context) {
    throw new Error('useTrackingPostalCode must be used within a TrackingPostalCodeProvider')
  }

  return context
}
